import { CartItem, useCartStore } from "../../store/CartStore";
import { useContext, useEffect, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { API_HOST, checkoutRoute, locationRoute, menuRoute } from "../../App";
import axios from "axios";
import { useTrackingEvent } from "@/hooks/useTrackingEvent";
import { allMenusQueryOptions } from "@/hooks/useMenu";
import { locationInfoQueryOptions } from "@/hooks/useLocationInfo";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import Loader from "@/components/Loader";
import { LocationConfigurationContext } from "@/providers/LocationConfigurationProvider";
import { NEWSLETTER_PROVIDERS } from "@/lib/newsletter";
import { PhoneInput } from "@/components/ui/custom/phone-input";
import { Button } from "@/components/ui/button";
import { CircleAlertIcon, CircleCheckIcon } from "lucide-react";
import { getCountries, isPossiblePhoneNumber } from "react-phone-number-input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { AlertDialogTitle } from "@radix-ui/react-alert-dialog";
import TimePicker from "@/components/ui/custom/time-input";
// import { useTableOpen } from "@/hooks/useTables";

type ENABLED_PROVIDERS = "nets"; //| "viva";

type NetsPaymentReferencePayload = {
  testHost: string;
  email: string;
  phone?: string;
  name?: string;
  cart: CartItem[];
  table: string;
  isTakeAway: boolean;
  pickupTime: string;
};

type VivaOrderPayload = {
  amount: number;
  customerTrns: string;
  customer: {
    email: string;
    fullName: string;
    phone: string;
    countryCode: string;
    requestLang: `${string}-${string}`;
  };
  paymentTimeout: number;
  preauth: boolean;
  allowRecurring: boolean;
  maxInstallments: number;
  paymentNotification: boolean;
  tipAmount: number;
  disableExactAmount: boolean;
  disableCash: boolean;
  disableWallet: boolean;
  sourceCode: string;
  merchantTrns: string;
  tags: string[];
  cardTokens: string[];
};

type PaymentPayload =
  | {
      type: "nets";
      payload: NetsPaymentReferencePayload;
    }
  | {
      type: "viva";
      payload: VivaOrderPayload;
    };

export function CheckoutPage() {
  const { cart, table, price, mode } = useCartStore();

  const isTakeAway = mode === "TAKE_AWAY";
  const { locationId } = locationRoute.useParams();
  const { menuId } = menuRoute.useParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [email, setEmail] = useState("");
  const [invalidEmail, setInvalidEmail] = useState(false);

  const [phone, setPhone] = useState("");
  const [invalidPhone, setInvalidPhone] = useState(false);

  const [name, setName] = useState("");
  const [invalidName, setInvalidName] = useState(false);

  const [selectedTime, setSelectedTime] = useState<string>("");
  const [invalidTime, setInvalidTime] = useState(false);

  const [showCheckout, setShowCheckout] = useState(false);

  const [isPaymentIdFetched, setPaymentIdFetched] = useState(false);
  const [paymentError, setPaymentError] = useState(false);
  const { history } = useRouter();
  const tracking = useTrackingEvent();
  const menusQuery = useSuspenseQuery(allMenusQueryOptions(locationId));
  const menus = menusQuery.data;

  const locationInfoQuery = useSuspenseQuery(
    locationInfoQueryOptions(locationId),
  );
  const locationInfo = locationInfoQuery.data;

  const menu = menus.find((menu) => menu.id === menuId) ?? menus[0];

  const isSimphony = menu.isSimphony;

  const tableQueryParam = isSimphony ? `"${table}"` : table;

  const {
    newsletterSignUp,
    enablePhoneNumberInCheckout,
    enableNameInCheckout,
  } = useContext(LocationConfigurationContext);

  const isLocationPickupAtCounter =
    locationInfo.defaultOnPremiseDeliveryMethod === "PICKUP_AT_COUNTER";

  const isCartItemPickupAtCounter = cart.some((cartItem) =>
    menus.some((menu) =>
      menu.categories.some(
        (category) =>
          category.defaultOnPremiseDeliveryMethod === "PICKUP_AT_COUNTER" &&
          category.products.some(
            (product) => product.id === cartItem.productId,
          ),
      ),
    ),
  );

  const showPhoneInput =
    isTakeAway ||
    enablePhoneNumberInCheckout ||
    isLocationPickupAtCounter ||
    isCartItemPickupAtCounter;

  const hasNewsletterSignup = newsletterSignUp?.enabled === true;

  const [newsletterConsent, setNewsletterConsent] = useState<boolean>(false);

  // const { data: isChequeOpen, isLoading } = useTableOpen(menuId, table);

  useEffect(() => {
    if (cart.length === 0) {
      console.log("No items in cart, navigating back to menu");
      // NavigateTo introduced a weird infinite re-direct loop, where window.location.href doesn't.
      window.location.href = `/${locationId}/${menuId}${
        table ? `?table=${table}` : ""
      }`;
    }
  }, [cart]);

  // Hacky way to ensure there are no latent iframes from previous checkouts
  history.subscribe(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentId = urlParams.get("paymentId");
    const status = urlParams.get("status");

    if (paymentId && status) {
      if (status === "100") {
        window.location.href = `/${locationId}/success/${paymentId}`;
      } else {
        if (location.pathname != checkoutRoute.to) {
          window.location.reload();
        }
      }
    }

    if (location.pathname != checkoutRoute.to) {
      setShowCheckout(false);
      window.location.reload();
    }
  });

  const validateEmail = (email: string) => {
    var re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    if (!showPhoneInput) return true;

    return isPossiblePhoneNumber(phone);
  };

  const validateName = (name: string): boolean => {
    if (!enableNameInCheckout) return true;
    return name.trim().length > 0;
  };

  const validateTime = (time: string): boolean => {
    if (!isTakeAway) return true;

    if (time === "") {
      return false;
    }

    // If it's an ISO string, validate it's a valid date
    if (time.includes("T")) {
      const date = new Date(time);
      return !isNaN(date.getTime());
    }

    return true;
  };

  const getPickupTime = (timeString: string): string => {
    if (!timeString || timeString === "") {
      return "";
    }

    // If it's already an ISO string from the time picker, use it directly
    if (timeString.includes("T")) {
      return timeString;
    }

    // Fallback for legacy format (shouldn't happen with new time picker)
    if (timeString === "asap") {
      return new Date().toISOString();
    }

    return "";
  };

  const checkoutProviders: Record<
    ENABLED_PROVIDERS,
    {
      key: string;
      init: (paymentId: string, config?: any) => void;
      getPaymentReference: (body: PaymentPayload) => Promise<{
        paymentId?: string;
        redirectUrl?: string;
      }>;
    }
  > = {
    nets: {
      key: "nets",
      init: initializeNets,
      getPaymentReference: getNetsPaymentId,
    },
    // viva: {
    //   key: "viva",
    //   init: initializeViva,
    //   getPaymentReference: createVivaOrder,
    // },
  };

  async function getNetsPaymentId(data: PaymentPayload): Promise<{
    paymentId?: string;
    redirectUrl?: string;
  }> {
    if (data.type === "viva")
      throw new Error("Invalid payment type, received Viva, expected Nets");

    const payload = data.payload;

    const response = await axios.post(
      `${API_HOST}/v1/payments/${locationId}/${
        isSimphony ? "simphony/" : ""
      }generatePayment`,
      {
        ...payload,
        cart: payload.cart.map((item) => {
          return {
            ...item,
            archived: false,
          };
        }),
      },
    );

    const responseData = await response.data;

    const { paymentId, redirectUrl } = responseData;

    return { paymentId, redirectUrl };
  }

  // async function createVivaOrder(payload: VivaOrderPayload): Promise<string> {
  //   const response = await fetch(
  //     `https://widding.ngrok.dev/api/payments/viva/order`,
  //     {
  //       method: "POST",
  //       body: JSON.stringify(payload),
  //     }
  //   );

  //   const { orderCode } = await response.json();

  //   return orderCode.toString();
  // }

  function initializeNets(
    paymentId: string,
    options: {
      checkoutKey: string;
      paymentId: string;
      containerId: string;
      merchantHandlesConsumerData: boolean;
    },
  ) {
    // @ts-ignore
    const checkout = new Dibs.Checkout(options);

    checkout.on("payment-completed", function (response: any) {
      console.log("payment-completed response");
      console.log(response);
      window.location.href = `/${locationId}/success/${paymentId}`;
    });
    checkout.on("payment-cancelled", function (response: any) {
      window.location.href = `/${locationId}/${menuId}?table=${tableQueryParam}&openCart=true`;
      console.log(response);
    });

    console.log("checkout", checkout);
  }

  // function initializeViva(orderCode: string) {
  //   const checkoutColor = "e2b9a7";
  //   window.location.href = `https://demo.vivapayments.com/web/checkout?ref=${orderCode}&color=${checkoutColor}`;
  // }

  useEffect(() => {
    const provider = checkoutProviders["nets"];
    const paymentReferencePayload: PaymentPayload =
      provider.key === "nets"
        ? {
            type: "nets",
            payload: {
              testHost: window.location.host,
              email,
              phone,
              name,
              cart,
              table: table ?? "",
              isTakeAway,
              pickupTime: getPickupTime(selectedTime),
            },
          }
        : {
            type: "viva",
            payload: {
              amount: Number(price.toString() + "00"),
              customerTrns: `${menu.locationName} | TableTaxi`,
              customer: {
                email: email,
                fullName: "",
                phone: "",
                countryCode: "da",
                requestLang: "da-DK",
              },
              paymentTimeout: 300,
              preauth: false,
              allowRecurring: false,
              maxInstallments: 1,
              paymentNotification: false,
              tipAmount: 0,
              disableExactAmount: false,
              disableCash: true,
              disableWallet: true,
              sourceCode: "5908",
              merchantTrns: "TableTaxi Test",
              tags: [],
              cardTokens: [],
            },
          };

    if (!isPaymentIdFetched && showCheckout) {
      setIsLoading(true);
      provider
        .getPaymentReference(paymentReferencePayload)
        .then(({ paymentId, redirectUrl }) => {
          if (redirectUrl) {
            if (redirectUrl.startsWith("http")) {
              return (window.location.href = redirectUrl);
            }

            return (window.location.href = "/" + redirectUrl);
          }

          console.log(paymentId);
          if (paymentId) {
            setPaymentIdFetched(true);

            const providerOptions =
              provider.key === "nets"
                ? {
                    checkoutKey: menu.netsCheckoutKey,
                    paymentId: paymentId,
                    containerId: "checkout-container-div",
                    merchantHandlesConsumerData: false,
                  }
                : null;

            provider.init(paymentId, providerOptions);
            // Wait 500ms & hide loading state
            setTimeout(() => {
              window.scrollTo(0, 0); // make sure we scroll to the top
              setIsLoading(false);
            }, 500);
          } else {
            console.log("Expected a paymentId"); // No paymentId provided,
            window.location.href = `/${locationId}/${menuId}?table=${tableQueryParam}&openCart=true`;
          }
        })
        .catch((error) => {
          console.error("Error fetching payment reference:", error);
          setTimeout(() => {
            setPaymentError(true);
            setIsLoading(false);
          }, 5000);
        });
    }
  }, [isPaymentIdFetched, showCheckout]);

  const isValid =
    validateEmail(email) &&
    validatePhone(phone) &&
    validateName(name) &&
    validateTime(selectedTime);

  const countryCodes = getCountries()?.sort((a, b) => {
    // Sort countries so those in suggested order are first
    const suggestedOrder = ["DK", "SE", "NO", "DE", "FR", "NL", "GB", "US"];
    const aIndex = suggestedOrder.indexOf(a);
    const bIndex = suggestedOrder.indexOf(b);

    if (aIndex === -1 && bIndex === -1) {
      return 0;
    }

    if (aIndex === -1) {
      return 1;
    }

    if (bIndex === -1) {
      return -1;
    }

    return aIndex - bIndex;
  });

  return (
    <AlertDialog open={true}>
      {showCheckout ? null : (
        <AlertDialogContent id="checkout-dialog">
          <form
            className="flex flex-col gap-y-4 md:max-w-xl mx-auto"
            onSubmit={async (event) => {
              event.preventDefault();
              const isValid =
                validateEmail(email) &&
                validatePhone(phone) &&
                validateTime(selectedTime);

              if (!isValid) {
                if (!validateEmail(email)) {
                  setInvalidEmail(true);
                }
                if (!validatePhone(phone)) {
                  setInvalidPhone(true);
                }
                if (!validateName(name)) {
                  setInvalidName(true);
                }
                if (!validateTime(selectedTime)) {
                  setInvalidTime(true);
                }

                alert("Invalid input, please check your details");
                return;
              }

              if (isValid) {
                try {
                  tracking.capture("checkout_started", undefined, {
                    value: price,
                  });
                } catch (error) {
                  console.error(
                    "Error capturing checkout_started event:",
                    error,
                  );
                }

                setIsLoading(true);

                if (hasNewsletterSignup && newsletterConsent === true) {
                  const provider = newsletterSignUp.provider;

                  if (provider === "klaviyo") {
                    const { companyId, listId, locationTitle } =
                      newsletterSignUp.config;
                    const signedUp =
                      await NEWSLETTER_PROVIDERS.klaviyo.subscribe(
                        email,
                        companyId,
                        listId,
                        locationTitle,
                      );

                    if (!signedUp) {
                      console.error(
                        "Something went wrong with sign up, carry on to checkout",
                      );
                    }
                  }
                }
                setShowCheckout(true);
                setIsLoading(false);
                return;
              }
            }}
          >
            <header className="flex flex-col">
              <AlertDialogTitle className="text-lg font-medium">
                Contact information
              </AlertDialogTitle>

              <p>To continue, please confirm your contact information</p>
            </header>
            <div className="flex flex-col gap-y-4">
              {enableNameInCheckout && (
                <fieldset className="flex flex-col gap-y-2">
                  <p className="whitespace-pre-line">Name</p>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Full name"
                      className="text-[16px]"
                      value={name}
                      onChange={(e) => {
                        setInvalidName(false);
                        setName(e.target.value);
                      }}
                    />
                    {validateName(name) && name && (
                      <CircleCheckIcon
                        className="absolute top-1/2 -translate-y-1/2 right-2 text-green-600"
                        size={22}
                      />
                    )}
                  </div>
                  {invalidName && (
                    <p className="text-red-500 text-xs text-left whitespace-pre-line">
                      Please enter your name
                    </p>
                  )}
                  <p className="whitespace-pre-line text-xs text-muted-foreground text-left">
                    Your name will be called when your order is ready.
                  </p>
                </fieldset>
              )}

              <fieldset className="flex flex-col gap-y-2">
                <p className="whitespace-pre-line">{`Email`}</p>
                <div className="relative">
                  <Input
                    type="email"
                    placeholder="name@example.dk"
                    //autoFocus
                    //autoComplete="email"
                    className="text-[16px]"
                    value={email}
                    onChange={(e) => {
                      setInvalidEmail(false);
                      setEmail(e.target.value);
                    }}
                    tabIndex={0}
                  />
                  {validateEmail(email) && (
                    <CircleCheckIcon
                      className="absolute top-1/2 -translate-y-1/2 right-2 text-green-600"
                      size={22}
                    />
                  )}
                </div>
                {invalidEmail && (
                  <p className="text-red-500 text-xs text-left whitespace-pre-line">
                    Please enter a valid email address
                  </p>
                )}
                <p className="whitespace-pre-line text-xs text-muted-foreground text-left">
                  {hasNewsletterSignup
                    ? "Your e-mail is only used for sending you a receipt. You can optionally sign up for our newletter below."
                    : `Your e-mail is only used for sending you a receipt. It will not be used for any other purpose.`}
                </p>
              </fieldset>

              {showPhoneInput && (
                <fieldset className="flex flex-col gap-y-2">
                  <p className="whitespace-pre-line">{`Phone number`}</p>

                  <div className="relative">
                    <PhoneInput
                      defaultCountry="DK"
                      international
                      autoComplete="tel"
                      focusInputOnCountrySelection
                      countryCallingCodeEditable={false}
                      value={phone}
                      onChange={(value) => {
                        setInvalidPhone(false);
                        setPhone(value);
                      }}
                      countryOptionsOrder={countryCodes}
                    />
                    {phone && validatePhone(phone) ? (
                      <CircleCheckIcon
                        className="absolute top-1/2 -translate-y-1/2 right-2 text-green-600"
                        size={22}
                      />
                    ) : phone && !validatePhone(phone) ? (
                      <CircleAlertIcon
                        className="absolute top-1/2 -translate-y-1/2 right-2 text-red-500"
                        size={22}
                      />
                    ) : null}
                  </div>

                  {invalidPhone && (
                    <p className="text-red-500 text-xs text-left whitespace-pre-line">
                      Please enter a valid phone number
                    </p>
                  )}
                  <p className="whitespace-pre-line text-xs text-muted-foreground text-left">
                    Your phone number is used to contact you if needed. Please
                    make sure it's correct.
                  </p>
                </fieldset>
              )}

              {isTakeAway && (
                <fieldset>
                  <TimePicker
                    selectedTime={selectedTime}
                    onTimeSelect={setSelectedTime}
                    isValid={
                      selectedTime ? validateTime(selectedTime) : undefined
                    }
                  />

                  {invalidTime && (
                    <p className="text-red-500 text-xs text-left whitespace-pre-line">
                      Please select a time for pick-up
                    </p>
                  )}
                </fieldset>
              )}
            </div>
            <footer className="flex flex-col gap-y-2">
              <Button
                className="bg-primary"
                disabled={isLoading || isValid == false}
                type="submit"
              >
                {isValid
                  ? isLoading
                    ? "Please wait..."
                    : "Go to checkout"
                  : "Please enter your info"}
              </Button>

              <Button
                type="button"
                variant={"link"}
                onClick={() =>
                  (window.location.href = tableQueryParam
                    ? `/${locationId}/${menuId}?table=${tableQueryParam}&openCart=true`
                    : `/${locationId}/${menuId}?openCart=true`)
                }
              >
                Go back to menu
              </Button>

              {hasNewsletterSignup && (
                <div className="p-1 border-muted-foreground rounded-sm flex gap-1 border-solid">
                  <input
                    id="newsletterConsent"
                    type="checkbox"
                    name="newsletterConsent"
                    checked={newsletterConsent}
                    onChange={() => setNewsletterConsent(!newsletterConsent)}
                  />
                  <label
                    htmlFor="newsletterConsent"
                    className="text-xs text-muted-foreground"
                  >
                    {newsletterSignUp.config?.signupMessage}
                  </label>
                </div>
              )}
            </footer>
          </form>
        </AlertDialogContent>
      )}

      {paymentError && (
        <AlertDialogContent id={"error-dialog"}>
          <div className="flex flex-col gap-y-2 text-center">
            <AlertDialogTitle className="text-xl font-medium tracking-tight">
              An error occurred
            </AlertDialogTitle>
            <p className="text-muted-foreground">
              Please try again later, or contact staff is the issue persists.
            </p>
          </div>
          <AlertDialogFooter className="flex flex-row items-center justify-center gap-y-2">
            <AlertDialogAction
              className="bg-primary "
              onClick={() =>
                (window.location.href = tableQueryParam
                  ? `/${locationId}/${menuId}?table=${tableQueryParam}&openCart=true`
                  : `/${locationId}/${menuId}?openCart=true`)
              }
            >
              Return to menu
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      )}

      {isLoading && (
        <AlertDialogContent
          id="loading-dialog"
          className="p-8 max-w-full md:max-w-80"
        >
          <AlertDialogTitle className="sr-only">Loading</AlertDialogTitle>
          <Loader />
        </AlertDialogContent>
      )}
    </AlertDialog>
  );
}
